import { CampaignCardActions } from '@/dashboards/CampaignPlanner/components/CampaignCard/CampaignCardActions';
import { formatCampaignTargetValue } from '@/dashboards/CampaignPlanner/helpers/formatCampaignTargetValue';
import { OPTIMISATION_BASE_STYLES } from '@/dashboards/CampaignPlanner/helpers/optimisationBaseStyles';
import { useCampaignPlannerContext } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerContext';
import { useCampaignPlannerUrl } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerUrl';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { Campaign } from '@/dashboards/CampaignPlanner/types';
import { DateFormat, formatDate } from '@/utils/dateUtils';
import { dashboardRootRoute, toPath } from '@/utils/routes';
import { Typography } from '@analytical-alley/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
    addMonths,
    differenceInDays,
    endOfMonth,
    format,
    isWithinInterval,
    startOfMonth,
} from 'date-fns';
import { useMemo, useRef, useEffect } from 'react';

interface Props {
    campaigns: Campaign[];
}

export const CampaignPlannerTimeline = ({ campaigns }: Props) => {
    const timelineRef = useRef<HTMLDivElement>(null);
    const {
        params: { projectId },
    } = useCampaignPlannerUrl();
    const { dashboardId } = useSearch({ strict: false });
    const navigate = useNavigate();

    const {
        setRequestCampaign,
        setDeleteCandidate,
        setSaveAsCampaignReference,
        setShowSaveCampaignDialog,
    } = useCampaignPlannerContext();
    const { contributionFormatter } = useDashboardGlobalContext();

    const navigateToCampaign = async (campaign: Campaign) => {
        await navigate({
            to: toPath(dashboardRootRoute, '/campaign-optimization/$campaignId'),
            params: { projectId, campaignId: campaign.id },
            search: {
                dashboardId,
                optimizationBase: campaign.optimizationBase,
                budget: campaign.budget,
                startDate: formatDate(campaign.startDate, DateFormat.URL),
                endDate: formatDate(campaign.endDate, DateFormat.URL),
            },
        });
    };

    // Determine the date range for the timeline
    const { minDate, maxDate, months } = useMemo(() => {
        if (campaigns.length === 0) {
            const now = new Date();
            return {
                minDate: startOfMonth(now),
                maxDate: endOfMonth(addMonths(now, 5)),
                months: Array.from({ length: 6 }).map((_, i) =>
                    addMonths(startOfMonth(now), i),
                ),
            };
        }

        const dates = campaigns.flatMap((c) => [
            new Date(c.startDate),
            new Date(c.endDate),
        ]);
        const start = startOfMonth(new Date(Math.min(...dates.map((d) => d.getTime()))));
        const end = endOfMonth(new Date(Math.max(...dates.map((d) => d.getTime()))));

        // Ensure at least 6 months are shown
        let max = end;
        const monthDiff = differenceInDays(end, start) / 30;
        if (monthDiff < 5) {
            max = endOfMonth(addMonths(start, 5));
        }

        const monthList: Date[] = [];
        let current = start;
        while (current <= max) {
            monthList.push(current);
            current = addMonths(current, 1);
        }

        return { minDate: start, maxDate: max, months: monthList };
    }, [campaigns]);

    const totalDays = useMemo(
        () => differenceInDays(maxDate, minDate) + 1,
        [minDate, maxDate],
    );

    // Scroll to today if within range
    useEffect(() => {
        if (timelineRef.current) {
            const today = new Date();
            if (isWithinInterval(today, { start: minDate, end: maxDate })) {
                const dayOffset = differenceInDays(today, minDate);
                const scrollLeft = (dayOffset / totalDays) * timelineRef.current.scrollWidth - 200;
                timelineRef.current.scrollLeft = Math.max(0, scrollLeft);
            }
        }
    }, [minDate, maxDate, totalDays]);

    return (
        <div className="flex flex-col border border-divider rounded-xl overflow-hidden glass">
            <div className="flex overflow-hidden">
                {/* Fixed Column: Campaign Names & Details */}
                <div className="w-96 shrink-0 border-r border-divider glass z-10">
                    <div className="h-12 border-b border-divider flex items-center px-4 font-semibold text-sm text-gray-500 bg-gray-50/50">
                        <div className="flex-1">Campaign</div>
                        <div className="w-24 text-right pr-12">Target</div>
                    </div>
                    {campaigns.map((campaign) => {
                        const isDraft = campaign.status === 'draft';
                        const optimisationBaseStyle =
                            OPTIMISATION_BASE_STYLES[campaign.optimizationBase];
                        return (
                            <div
                                key={campaign.id}
                                className="h-16 border-b border-divider flex items-center px-4 hover:bg-accent/20 transition-colors cursor-pointer group"
                                onClick={() => navigateToCampaign(campaign)}
                            >
                                <div className="flex-1 min-w-0 pr-2">
                                    <Typography
                                        variant="bodyS"
                                        className="font-semibold truncate group-hover:text-dark transition-colors"
                                        title={campaign.name}
                                    >
                                        {campaign.name}
                                    </Typography>
                                    <span
                                        className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${optimisationBaseStyle.tag}`}
                                        title={optimisationBaseStyle.label}
                                    >
                                        {optimisationBaseStyle.label}
                                    </span>
                                </div>
                                <div className="w-24 text-right">
                                    <Typography variant="bodyXS" className="text-gray-600">
                                        {formatCampaignTargetValue(
                                            campaign.optimizationBase,
                                            campaign.budget,
                                            contributionFormatter,
                                        )}
                                    </Typography>
                                </div>
                                <div
                                    className="ml-4"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <CampaignCardActions
                                        canRequestReview={isDraft}
                                        canDelete={isDraft}
                                        onSaveAs={() => {
                                            setSaveAsCampaignReference(campaign);
                                            setShowSaveCampaignDialog(true);
                                        }}
                                        onRequestReview={() =>
                                            setRequestCampaign(campaign)
                                        }
                                        onDelete={() => setDeleteCandidate(campaign.id)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Scrollable Column: Timeline Grid */}
                <div
                    ref={timelineRef}
                    className="flex-1 overflow-x-auto scrollbar-hide relative"
                    style={{ cursor: 'grab' }}
                >
                    <div style={{ width: `${totalDays * 12}px`, minWidth: '100%' }}>
                        {/* Header: Months */}
                        <div className="h-12 border-b border-divider flex bg-gray-50/50">
                            {months.map((month) => {
                                const monthDays = differenceInDays(
                                    endOfMonth(month),
                                    month,
                                ) + 1;
                                // Adjust if it's the first or last month (partial)
                                const displayDays = differenceInDays(
                                    month === months[months.length - 1] ? maxDate : endOfMonth(month),
                                    month === months[0] ? minDate : month
                                ) + 1;

                                return (
                                    <div
                                        key={month.getTime()}
                                        className="border-r border-divider h-full flex items-center px-3 text-sm font-medium text-gray-600 shrink-0"
                                        style={{ width: `${displayDays * 12}px` }}
                                    >
                                        {format(month, 'MMMM yyyy')}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Grid Rows */}
                        {campaigns.map((campaign) => {
                            const start = new Date(campaign.startDate);
                            const end = new Date(campaign.endDate);
                            const leftOffset = differenceInDays(start, minDate);
                            const duration = differenceInDays(end, start) + 1;
                            const optimisationBaseStyle =
                                OPTIMISATION_BASE_STYLES[
                                    campaign.optimizationBase
                                ];

                            return (
                                <div
                                    key={campaign.id}
                                    className="h-16 border-b border-divider relative group hover:bg-accent/10 transition-colors"
                                >
                                    {/* Grid Lines (Vertical) */}
                                    <div className="absolute inset-0 flex">
                                        {months.map((month) => {
                                             const displayDays = differenceInDays(
                                                month === months[months.length - 1] ? maxDate : endOfMonth(month),
                                                month === months[0] ? minDate : month
                                            ) + 1;
                                            return (
                                                <div
                                                    key={month.getTime()}
                                                    className="h-full border-r border-divider/30 shrink-0"
                                                    style={{ width: `${displayDays * 12}px` }}
                                                />
                                            );
                                        })}
                                    </div>

                                    {/* Campaign Bar */}
                                    <div
                                        className={`absolute top-4 h-8 rounded-full shadow-sm flex items-center px-3 z-0 transition-all group-hover:shadow-md cursor-pointer ${optimisationBaseStyle.tag} bg-opacity-90 backdrop-blur-xs`}
                                        style={{
                                            left: `${leftOffset * 12}px`,
                                            width: `${duration * 12}px`,
                                            minWidth: '40px',
                                        }}
                                        onClick={() => navigateToCampaign(campaign)}
                                    >
                                        <span className="text-[10px] font-bold whitespace-nowrap overflow-hidden text-ellipsis">
                                            {formatDate(campaign.startDate, DateFormat.Normal)} - {formatDate(campaign.endDate, DateFormat.Normal)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Today Marker */}
                        {isWithinInterval(new Date(), { start: minDate, end: maxDate }) && (
                            <div
                                className="absolute top-0 bottom-0 w-px bg-blue-500 z-20 pointer-events-none"
                                style={{
                                    left: `${differenceInDays(new Date(), minDate) * 12}px`,
                                }}
                            >
                                <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white shadow-sm" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
